// ── Firestore-Kompatibilitäts-Schicht (Web ↔ Native) ─────────────────────────
//
// Problem: Das Firebase-JS-SDK kann auf der nativen `capacitor://localhost`-
// Herkunft nicht netzwerken (auth/network-request-failed, Firestore-Listener
// hängt). Lösung: Auf NATIVE läuft Firestore über das native Plugin
// `@capacitor-firebase/firestore` (native iOS/Android Firebase-SDK via URLSession,
// umgeht WKWebView komplett). Im WEB bleibt alles 1:1 das JS-SDK.
//
// Diese Datei stellt EXAKT die `firebase/firestore`-Funktionen bereit, die die App
// nutzt — gleiche Signaturen. Die 14 Aufrufer importieren statt aus
// 'firebase/firestore' aus dieser Datei; ihre Funktionskörper bleiben unverändert.
//
// WICHTIG: Web-Pfad ist byte-identisch zum SDK (reine Delegation). Nur der
// Native-Pfad ist neu.
import * as FB from 'firebase/firestore';
import { FirebaseFirestore, FieldValue } from '@capacitor-firebase/firestore';

const NATIVE = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

// ── Auto-ID-Generator (Firestore-kompatibel, 20 Zeichen) ─────────────────────
const _ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
function _autoId() {
  let id = '';
  for (let i = 0; i < 20; i++) id += _ID_ALPHABET[Math.floor(Math.random() * _ID_ALPHABET.length)];
  return id;
}

// ── Ref-Deskriptoren (nur nativ; tragen Pfad + Query-Constraints) ────────────
const _isRef = (x) => x && typeof x === 'object' && (x.__t === 'col' || x.__t === 'doc' || x.__t === 'query');

function nCollection(_db, ...segs) {
  // collection(db, 'a','b',...) ODER collection(docRef, 'sub')
  let base = '';
  let rest = segs;
  if (_isRef(_db)) { base = _db.path; } // collection(docRef, ...)
  const path = [base, ...rest].filter(Boolean).join('/');
  return { __t: 'col', path };
}

function nDoc(arg, ...segs) {
  // doc(db, 'a','b',...) | doc(colRef)  -> Auto-ID | doc(colRef, 'id')
  if (_isRef(arg)) {
    if (arg.__t === 'col') {
      const id = segs.length ? segs.join('/') : _autoId();
      return { __t: 'doc', path: `${arg.path}/${id}` };
    }
    // doc(docRef, ...) ungewöhnlich — Pfad anhängen
    return { __t: 'doc', path: [arg.path, ...segs].filter(Boolean).join('/') };
  }
  // doc(db, ...segs)
  return { __t: 'doc', path: segs.join('/') };
}

function nCollectionGroup(_db, id) {
  return { __t: 'cg', path: id };
}

function nWhere(fieldPath, opStr, value) { return { type: 'where', fieldPath, opStr, value }; }
function nOrderBy(fieldPath, directionStr = 'asc') { return { type: 'orderBy', fieldPath, directionStr }; }
function nLimit(n) { return { type: 'limit', limit: n }; }

function nQuery(ref, ...constraints) {
  const prev = ref.__t === 'query' ? ref.constraints : [];
  return { __t: 'query', path: ref.path, group: ref.__t === 'cg' || ref.group, constraints: [...prev, ...constraints] };
}

// Constraints in Plugin-Form aufteilen: where → compositeFilter(and), Rest → queryConstraints.
function _splitConstraints(constraints = []) {
  const wheres = constraints.filter((c) => c.type === 'where');
  const others = constraints.filter((c) => c.type !== 'where');
  const compositeFilter = wheres.length
    ? { type: 'and', queryConstraints: wheres }
    : undefined;
  return { compositeFilter, queryConstraints: others.length ? others : undefined };
}

function _resolveCollection(x) {
  if (x.__t === 'query') {
    const { compositeFilter, queryConstraints } = _splitConstraints(x.constraints);
    return { reference: x.path, compositeFilter, queryConstraints, group: !!x.group };
  }
  return { reference: x.path, group: x.__t === 'cg' };
}

// ── Snapshot-Hüllen (gleiche API wie das JS-SDK) ─────────────────────────────
function _docSnap(s) {
  // s: { id, path, data } vom Plugin
  const data = s?.data ?? null;
  return {
    id: s?.id,
    ref: { __t: 'doc', path: s?.path },
    exists: () => data != null,
    data: () => (data == null ? undefined : data),
    get: (k) => (data == null ? undefined : data[k]),
  };
}
function _querySnap(snapshots = []) {
  const docs = snapshots.map(_docSnap);
  return {
    docs,
    size: docs.length,
    empty: docs.length === 0,
    forEach: (fn) => docs.forEach(fn),
  };
}

// ── Native Operationen ───────────────────────────────────────────────────────
async function nGetDoc(docRef) {
  const res = await FirebaseFirestore.getDocument({ reference: docRef.path });
  return _docSnap(res.snapshot);
}
async function nGetDocs(qOrRef) {
  const { reference, compositeFilter, queryConstraints, group } = _resolveCollection(qOrRef);
  const opts = { reference, ...(compositeFilter ? { compositeFilter } : {}), ...(queryConstraints ? { queryConstraints } : {}) };
  const res = group
    ? await FirebaseFirestore.getCollectionGroup(opts)
    : await FirebaseFirestore.getCollection(opts);
  return _querySnap(res.snapshots);
}
async function nAddDoc(colRef, data) {
  const res = await FirebaseFirestore.addDocument({ reference: colRef.path, data });
  return { id: res.reference?.id, path: res.reference?.path };
}
async function nSetDoc(docRef, data, opts) {
  await FirebaseFirestore.setDocument({ reference: docRef.path, data, merge: !!opts?.merge });
}
async function nUpdateDoc(docRef, data) {
  await FirebaseFirestore.updateDocument({ reference: docRef.path, data });
}
async function nDeleteDoc(docRef) {
  await FirebaseFirestore.deleteDocument({ reference: docRef.path });
}

function nOnSnapshot(qOrRef, a, b) {
  // (ref, onNext, onError) ODER (ref, { next, error })
  const onNext = typeof a === 'function' ? a : a?.next;
  const onError = typeof a === 'function' ? b : a?.error;
  let cbId = null;
  let removed = false;
  let promise;

  if (qOrRef.__t === 'doc') {
    promise = FirebaseFirestore.addDocumentSnapshotListener(
      { reference: qOrRef.path },
      (event, err) => {
        if (err) { onError?.(err); return; }
        if (!event) return;
        onNext?.(_docSnap(event.snapshot));
      }
    );
  } else {
    const { reference, compositeFilter, queryConstraints, group } = _resolveCollection(qOrRef);
    const opts = { reference, ...(compositeFilter ? { compositeFilter } : {}), ...(queryConstraints ? { queryConstraints } : {}) };
    // WICHTIG: Methode NICHT in eine Variable lösen — sonst verliert sie das
    // this-Binding zum Plugin-Proxy ("undefined is not an object: this.plugin").
    const cb = (event, err) => {
      if (err) { onError?.(err); return; }
      if (!event) return;
      onNext?.(_querySnap(event.snapshots));
    };
    promise = group
      ? FirebaseFirestore.addCollectionGroupSnapshotListener(opts, cb)
      : FirebaseFirestore.addCollectionSnapshotListener(opts, cb);
  }

  promise
    .then((id) => {
      cbId = id;
      if (removed) FirebaseFirestore.removeSnapshotListener({ callbackId: id }).catch(() => {});
    })
    .catch((e) => onError?.(e));

  return () => {
    removed = true;
    if (cbId) FirebaseFirestore.removeSnapshotListener({ callbackId: cbId }).catch(() => {});
  };
}

function nWriteBatch() {
  const operations = [];
  return {
    set: (docRef, data, opts) => { operations.push({ type: 'set', reference: docRef.path, data, ...(opts?.merge ? { options: { merge: true } } : {}) }); },
    update: (docRef, data) => { operations.push({ type: 'update', reference: docRef.path, data }); },
    delete: (docRef) => { operations.push({ type: 'delete', reference: docRef.path }); },
    commit: async () => { if (operations.length) await FirebaseFirestore.writeBatch({ operations }); },
  };
}

// runTransaction: das Plugin hat KEINE echte Transaktion. Wir bilden die JS-SDK-
// Semantik nach: tx.get sofort (await), tx.set/update/delete werden gesammelt und
// am Ende per writeBatch atomar geschrieben. NICHT race-sicher (kein Read-Lock/
// Retry) — die 2 Aufrufer (Mitgliedsnummer, VIP-Code) sind selten und zusätzlich
// durch firestore.rules serverseitig abgesichert.
async function nRunTransaction(_db, updateFn) {
  const operations = [];
  const tx = {
    get: async (docRef) => nGetDoc(docRef),
    set: (docRef, data, opts) => { operations.push({ type: 'set', reference: docRef.path, data, ...(opts?.merge ? { options: { merge: true } } : {}) }); },
    update: (docRef, data) => { operations.push({ type: 'update', reference: docRef.path, data }); },
    delete: (docRef) => { operations.push({ type: 'delete', reference: docRef.path }); },
  };
  const result = await updateFn(tx);
  if (operations.length) await FirebaseFirestore.writeBatch({ operations });
  return result;
}

// ── Exporte: pro Aufruf auf Web/Native verzweigen (NATIVE ist konstant) ──────
export const collection = (...a) => (NATIVE ? nCollection(...a) : FB.collection(...a));
export const collectionGroup = (...a) => (NATIVE ? nCollectionGroup(...a) : FB.collectionGroup(...a));
export const doc = (...a) => (NATIVE ? nDoc(...a) : FB.doc(...a));
export const query = (...a) => (NATIVE ? nQuery(...a) : FB.query(...a));
export const where = (...a) => (NATIVE ? nWhere(...a) : FB.where(...a));
export const orderBy = (...a) => (NATIVE ? nOrderBy(...a) : FB.orderBy(...a));
export const limit = (...a) => (NATIVE ? nLimit(...a) : FB.limit(...a));
export const onSnapshot = (...a) => (NATIVE ? nOnSnapshot(...a) : FB.onSnapshot(...a));
export const getDoc = (...a) => (NATIVE ? nGetDoc(...a) : FB.getDoc(...a));
export const getDocs = (...a) => (NATIVE ? nGetDocs(...a) : FB.getDocs(...a));
export const addDoc = (...a) => (NATIVE ? nAddDoc(...a) : FB.addDoc(...a));
export const setDoc = (...a) => (NATIVE ? nSetDoc(...a) : FB.setDoc(...a));
export const updateDoc = (...a) => (NATIVE ? nUpdateDoc(...a) : FB.updateDoc(...a));
export const deleteDoc = (...a) => (NATIVE ? nDeleteDoc(...a) : FB.deleteDoc(...a));
export const writeBatch = (...a) => (NATIVE ? nWriteBatch(...a) : FB.writeBatch(...a));
export const runTransaction = (...a) => (NATIVE ? nRunTransaction(...a) : FB.runTransaction(...a));
export const serverTimestamp = () => (NATIVE ? FieldValue.serverTimestamp() : FB.serverTimestamp());
export const increment = (n) => (NATIVE ? FieldValue.increment(n) : FB.increment(n));
export const arrayUnion = (...v) => (NATIVE ? FieldValue.arrayUnion(...v) : FB.arrayUnion(...v));
export const arrayRemove = (...v) => (NATIVE ? FieldValue.arrayRemove(...v) : FB.arrayRemove(...v));
export const deleteField = () => (NATIVE ? FieldValue.delete() : FB.deleteField());
